import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DozentService } from './dozent.service';
import { DozentSocketService } from './dozent-socket.service';
import { StateCleanupService } from './state-cleanup.service';

export interface DozentNotification {
  id: string;
  type: string;
  title?: string;
  body?: string;
  reference_id?: string;
  reference_type?: string;
  is_read: boolean;
  metadata?: any;
  created_at: string;
}

@Injectable({
  providedIn: 'root',
})
export class DozentNotificationService implements OnDestroy {
  notifications$ = new BehaviorSubject<DozentNotification[]>([]);
  unreadCount$ = new BehaviorSubject<number>(0);

  private socketSub: Subscription | null = null;

  constructor(
    private dozentService: DozentService,
    private socketService: DozentSocketService,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());

    // Listen for real-time notifications
    this.socketSub = this.socketService.notification$.subscribe((data) => {
      const current = this.notifications$.value;
      if (current.some((n) => n.id === data?.id)) {
        return;
      }
      this.notifications$.next([data, ...current]);
      this.unreadCount$.next(this.unreadCount$.value + 1);
    });
  }

  loadNotifications() {
    this.dozentService.listNotifications().subscribe({
      next: (notifications: DozentNotification[]) => {
        this.notifications$.next(notifications);
        this.unreadCount$.next(notifications.filter((n) => !n.is_read).length);
      },
    });
  }

  refreshUnreadCount() {
    this.dozentService.getNotificationUnreadCount().subscribe({
      next: (count: number) => {
        this.unreadCount$.next(count);
      },
    });
  }

  markRead(notificationId: string) {
    this.dozentService.markNotificationRead(notificationId).subscribe({
      next: () => {
        const current = this.notifications$.value;
        const updated = current.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        );
        this.notifications$.next(updated);
        this.unreadCount$.next(Math.max(0, this.unreadCount$.value - 1));
      },
    });
  }

  markAllRead() {
    this.dozentService.markAllNotificationsRead().subscribe({
      next: () => {
        const current = this.notifications$.value.map((n) => ({
          ...n,
          is_read: true,
        }));
        this.notifications$.next(current);
        this.unreadCount$.next(0);
      },
    });
  }

  private resetState() {
    this.notifications$.next([]);
    this.unreadCount$.next(0);
  }

  ngOnDestroy() {
    this.socketSub?.unsubscribe();
  }
}
